import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Tabs, TabList, Tab, Select, Option, Modal, ModalDialog, Input, CircularProgress } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./phoneinbound.module.css";
import ReceiverRow from "./receiverrow";
import AddReceiverModal from "./addreceivermodal";
import ReceiverDetailModal from "./receiverdetailmodal";
import QueuePasswordModal from "./queuepasswordmodal";
import ManagerModePopover from "./managermodepopover";
import ManagerModeConfirmModal from "./managermodeconfirmmodal";

const toastOptions : any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored"
};

//정렬: 수신 ON 먼저 → ★담당 먼저 → 이름순. OFF는 맨 아래 (penalty 우선순위는 폐기)
const sortReceivers = (list : any[]) => {
    return [...list].sort((a, b) => {
        const ea = a.enabled ? 1 : 0;
        const eb = b.enabled ? 1 : 0;
        if (ea !== eb) return eb - ea;
        const ma = a.isManager ? 1 : 0;
        const mb = b.isManager ? 1 : 0;
        if (ma !== mb) return mb - ma;
        return String(a.name).localeCompare(String(b.name), "ko");
    });
};

//FLIP: 렌더 전후 행 위치 차이를 transform으로 되돌렸다가 풀어서 재정렬을 부드럽게
//위치는 컨테이너 기준 상대 좌표로 저장 (뷰포트 기준으로 하면 스크롤만 해도 "이동"으로 오인해 튕김)
//행 순서가 실제로 바뀐 렌더에서만 애니메이션 (1초 틱 등 단순 리렌더에서는 건너뜀)
const useFlip = (containerRef : React.RefObject<HTMLDivElement>) => {
    const positions = useRef<Map<string, { top : number; left : number }>>(new Map());
    const orderKey = useRef<string>("");
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const nodes = Array.from(container.querySelectorAll<HTMLElement>("[data-flip-id]"));
        const containerRect = container.getBoundingClientRect();
        const next = new Map<string, { top : number; left : number }>();
        const nextOrderKey = nodes.map((node) => node.dataset.flipId).join(",");
        const orderChanged = orderKey.current !== "" && orderKey.current !== nextOrderKey;
        nodes.forEach((node) => {
            const id = node.dataset.flipId as string;
            const rect = node.getBoundingClientRect();
            const pos = { top: rect.top - containerRect.top, left: rect.left - containerRect.left };
            next.set(id, pos);
            const prev = positions.current.get(id);
            if (orderChanged && prev) {
                const dx = prev.left - pos.left;
                const dy = prev.top - pos.top;
                if (dx !== 0 || dy !== 0) {
                    node.style.transition = "none";
                    node.style.transform = `translate(${dx}px, ${dy}px)`;
                    void node.offsetWidth;
                    node.style.transition = "transform 320ms ease";
                    node.style.transform = "";
                }
            }
        });
        positions.current = next;
        orderKey.current = nextOrderKey;
    });
};

//전화 수신 설정 페이지 (상담 예약 관리 > 전화 인바운드 설정하기)
const PhoneInboundSetting : React.FC<any> = (props) => {

    const [groups, setGroups] = useState<any[]>([]);
    const [pbxQueues, setPbxQueues] = useState<any[]>([]);
    const [canEditQueues, setCanEditQueues] = useState<boolean>(false);
    const [pbxConnected, setPbxConnected] = useState<boolean>(true);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [queues, setQueues] = useState<any[]>([]);
    const [receivers, setReceivers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [queueBarOpen, setQueueBarOpen] = useState<boolean>(false);
    const [addQueueOpen, setAddQueueOpen] = useState<boolean>(false);
    const [queueWarnings, setQueueWarnings] = useState<{ [queue : string] : string[] }>({});
    const [groupWarnings, setGroupWarnings] = useState<string[]>([]);
    const [managerCount, setManagerCount] = useState<number>(0);
    const [modeAnchor, setModeAnchor] = useState<{ el : HTMLElement; queue : string } | null>(null);
    const [modeConfirm, setModeConfirm] = useState<any>(null);
    const [now, setNow] = useState<number>(Date.now());

    const [addOpen, setAddOpen] = useState<boolean>(false);
    const [detailReceiver, setDetailReceiver] = useState<any>(null);
    const [passwordQueue, setPasswordQueue] = useState<any>(null);
    const [addGroupOpen, setAddGroupOpen] = useState<boolean>(false);
    const [newGroupName, setNewGroupName] = useState<string>("");
    const [creatingGroup, setCreatingGroup] = useState<boolean>(false);

    //진행 중인 낙관적 업데이트 (소켓 스냅샷이 해당 필드를 덮어쓰지 않도록)
    const pendingRef = useRef<{ [receiverId : number] : { enabled? : boolean; isManager? : boolean } }>({});
    const [pendingIds, setPendingIds] = useState<{ [receiverId : number] : boolean }>({});
    //담당 모드 낙관 갱신 중인 큐 (소켓 스냅샷이 되돌리지 않도록)
    const modePendingRef = useRef<{ [queue : string] : string }>({});
    const selectedGroupIdRef = useRef<number | null>(null);
    const hqListRef = useRef<HTMLDivElement>(null);
    const branchListRef = useRef<HTMLDivElement>(null);

    useFlip(hqListRef);
    useFlip(branchListRef);

    useEffect(() => {
        selectedGroupIdRef.current = selectedGroupId;
    }, [selectedGroupId]);

    //통화중 경과시간 카운트업용 1초 틱
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        loadGroups(true);
    }, []);

    useEffect(() => {
        if (!selectedGroupId) return;
        loadReceivers(selectedGroupId);
    }, [selectedGroupId]);

    //소켓: callcenter room 입장, receiver_update / pbx_connection 수신, 재접속 시 재입장
    useEffect(() => {
        const socket = props.socket;
        if (!socket) return;

        const join = () => {
            socket.emit("joinCallCenterRoom");
        };
        const onReceiverUpdate = (data : any) => {
            if (!data || data.groupId !== selectedGroupIdRef.current) return;
            applyServerReceivers(data.receivers || [], data.queues, data.pbxConnected, data.warnings, data.managerCount);
        };
        const onPbxConnection = (data : any) => {
            setPbxConnected(!!(data && data.connected));
        };

        join();
        socket.on("receiver_update", onReceiverUpdate);
        socket.on("pbx_connection", onPbxConnection);
        socket.on("connect", join);

        return () => {
            socket.off("receiver_update", onReceiverUpdate);
            socket.off("pbx_connection", onPbxConnection);
            socket.off("connect", join);
            socket.emit("leaveCallCenterRoom");
        };
    }, [props.socket]);

    const loadGroups = async (selectFirst : boolean) => {
        try {
            const result : any = await getFetch("/dashboard/callcenter/groups");
            if (result.message !== "success") {
                toast.error(result.message === "CALLCENTER_DISABLED" ? "콜센터 모듈이 서버에서 비활성화되어 있습니다" : "그룹 목록을 불러오지 못했습니다", toastOptions);
                setLoading(false);
                return;
            }
            setGroups(result.groups || []);
            setPbxQueues(result.pbxQueues || []);
            setCanEditQueues(!!result.canEditQueues);
            setPbxConnected(!!result.pbxConnected);
            if (selectFirst && result.groups && result.groups.length > 0) {
                setSelectedGroupId(result.groups[0].id);
            } else if (result.groups && result.groups.length === 0) {
                setLoading(false);
            }
        } catch (e) {
            console.log(e);
            toast.error("그룹 목록을 불러오지 못했습니다", toastOptions);
            setLoading(false);
        }
    };

    const loadReceivers = async (groupId : number) => {
        try {
            const result : any = await getFetch(`/dashboard/callcenter/receivers?groupId=${groupId}`);
            if (result.message === "success" && result.groupId === selectedGroupIdRef.current) {
                applyServerReceivers(result.receivers || [], result.queues, result.pbxConnected, result.warnings, result.managerCount);
            }
        } catch (e) {
            console.log(e);
            toast.error("수신자 목록을 불러오지 못했습니다", toastOptions);
        }
        setLoading(false);
    };

    //서버 스냅샷 반영 — 진행 중(pending) 필드는 로컬 값 유지
    const applyServerReceivers = (serverList : any[], serverQueues? : any[], serverPbxConnected? : boolean, serverWarnings? : string[], serverManagerCount? : number) => {
        const receivedAt = Date.now();
        setReceivers((prev) => serverList.map((r : any) => {
            const pending = pendingRef.current[r.id];
            const local = prev.find((x) => x.id === r.id);
            const merged = { ...r, _receivedAt: receivedAt };
            if (pending && local) {
                if (pending.enabled) merged.enabled = local.enabled;
                if (pending.isManager) merged.isManager = local.isManager;
            }
            return merged;
        }));
        if (serverQueues) {
            //담당 모드 낙관 갱신 중(modePendingRef)이면 그 큐의 managerMode 는 로컬 값 유지
            setQueues((prev) => serverQueues.map((q : any) => {
                const pendingMode = modePendingRef.current[q.queue];
                return pendingMode ? { ...q, managerMode: pendingMode } : q;
            }));
        }
        if (typeof serverPbxConnected === "boolean") {
            setPbxConnected(serverPbxConnected);
        }
        if (Array.isArray(serverWarnings)) {
            setGroupWarnings(serverWarnings);
        }
        if (typeof serverManagerCount === "number") {
            setManagerCount(serverManagerCount);
        }
    };

    const markPending = (receiverId : number, field : "enabled" | "isManager", on : boolean) => {
        const current = pendingRef.current[receiverId] || {};
        const next = { ...current, [field]: on };
        if (!next.enabled && !next.isManager) {
            delete pendingRef.current[receiverId];
        } else {
            pendingRef.current[receiverId] = next;
        }
        setPendingIds((prev) => {
            const copy = { ...prev };
            if (pendingRef.current[receiverId]) copy[receiverId] = true;
            else delete copy[receiverId];
            return copy;
        });
    };

    //수신 토글 — 낙관적 업데이트, 실패 시 롤백
    const toggleEnabled = async (receiver : any, checked : boolean) => {
        const prevEnabled = receiver.enabled;
        markPending(receiver.id, "enabled", true);
        setReceivers((prev) => prev.map((r) => r.id === receiver.id ? { ...r, enabled: checked ? 1 : 0 } : r));
        try {
            const result : any = await postFetch("/dashboard/callcenter/receivers/enabled", { receiverId: receiver.id, enabled: checked });
            if (result.message !== "success") {
                throw new Error(result.message);
            }
            //DB 반영 전에 시작된 보정 스냅샷이 뒤늦게 도착해도 되돌아가지 않도록 잠시 더 유지
            setTimeout(() => markPending(receiver.id, "enabled", false), 1500);
        } catch (e) {
            console.log(e);
            setReceivers((prev) => prev.map((r) => r.id === receiver.id ? { ...r, enabled: prevEnabled } : r));
            toast.error("교환기 반영 실패", toastOptions);
            markPending(receiver.id, "enabled", false);
        }
    };

    //★담당 토글 — 낙관적 업데이트, 실패 시 롤백 (본사팀만)
    const toggleManager = async (receiver : any, on : boolean) => {
        const prevManager = receiver.isManager;
        markPending(receiver.id, "isManager", true);
        setReceivers((prev) => prev.map((r) => r.id === receiver.id ? { ...r, isManager: on ? 1 : 0 } : r));
        setManagerCount((c) => Math.max(0, c + (on ? 1 : -1)));
        try {
            const result : any = await postFetch("/dashboard/callcenter/receivers/manager", { receiverId: receiver.id, isManager: on });
            if (result.message !== "success") {
                throw new Error(result.message);
            }
            if (result.warnings && result.warnings.length > 0) {
                toast.warning(result.warnings.join(" / "), toastOptions);
            }
            setTimeout(() => markPending(receiver.id, "isManager", false), 1500);
        } catch (e) {
            console.log(e);
            setReceivers((prev) => prev.map((r) => r.id === receiver.id ? { ...r, isManager: prevManager } : r));
            setManagerCount((c) => Math.max(0, c + (on ? -1 : 1)));
            toast.error("교환기 반영 실패", toastOptions);
            markPending(receiver.id, "isManager", false);
        }
    };

    //지점 담당 모드 전환 — 칩 팝오버와 지점 폰 행 pill 이 같은 함수 사용. 낙관 갱신 + 실패 롤백. 성공 여부 반환
    const saveManagerMode = async (queue : string, mode : string) : Promise<boolean> => {
        if (!selectedGroupId) return false;
        const prevMode = (queues.find((q : any) => q.queue === queue) || {}).managerMode || "hq";
        modePendingRef.current[queue] = mode;
        setQueues((prev) => prev.map((q : any) => q.queue === queue ? { ...q, managerMode: mode } : q));
        try {
            const result : any = await postFetch("/dashboard/callcenter/queues/managerMode", { groupId: selectedGroupId, queue, managerMode: mode });
            if (result.message !== "success") {
                throw new Error(result.message);
            }
            toast.success(mode === "branch" ? "지점 직접 수신으로 전환했습니다" : "본사 담당 모드로 전환했습니다", toastOptions);
            if (result.warnings && result.warnings.length > 0) {
                toast.warning(result.warnings.join(" / "), toastOptions);
            }
            setTimeout(() => { delete modePendingRef.current[queue]; }, 1500);
            return true;
        } catch (e : any) {
            console.log(e);
            delete modePendingRef.current[queue];
            setQueues((prev) => prev.map((q : any) => q.queue === queue ? { ...q, managerMode: prevMode } : q));
            toast.error(e && e.message === "noPairQueue" ? "담당자 큐가 교환기에 없어 전환할 수 없습니다" : "교환기 반영 실패", toastOptions);
            return false;
        }
    };

    //지점 폰 행 pill 클릭 → 확인 다이얼로그
    const requestModeToggle = (receiver : any, nextMode : string) => {
        const q = queues.find((x : any) => x.queue === receiver.branchQueue);
        if (!q) return;
        setModeConfirm({ queue: q.queue, queueName: q.name, nextMode, phoneName: `${receiver.name} ${receiver.extension}` });
    };

    //큐 추가 (권한자)
    const addQueue = async (queue : string) => {
        if (!selectedGroupId) return;
        setAddQueueOpen(false);
        try {
            const result : any = await postFetch("/dashboard/callcenter/queues/add", { groupId: selectedGroupId, queue });
            if (result.message === "success") {
                if (result.warnings && result.warnings.length > 0) {
                    setQueueWarnings((prev) => ({ ...prev, [queue]: result.warnings }));
                    toast.warning("큐를 추가했지만 설정 경고가 있습니다", toastOptions);
                } else {
                    toast.success("큐를 추가했습니다", toastOptions);
                }
                await loadGroups(false);
                await loadReceivers(selectedGroupId);
            } else {
                const text = result.message === "queueAlreadyInGroup" ? "이미 다른 그룹에 속한 큐입니다"
                    : result.message === "noQueue" ? "교환기에 없는 큐입니다"
                    : result.message === "protectedQueue" ? "관리 대상이 아닌 큐입니다"
                    : result.message === "pairQueueNotAllowed" ? "담당자 큐(원본+5) 번호는 직접 추가할 수 없습니다"
                    : result.message === "NOT_AUTHORIZED" ? "큐 편집 권한이 없습니다"
                    : "교환기 반영 실패";
                toast.error(text, toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("교환기 반영 실패", toastOptions);
        }
    };

    const onQueueRemoved = async (queue : string) => {
        setQueueWarnings((prev) => {
            const copy = { ...prev };
            delete copy[queue];
            return copy;
        });
        await loadGroups(false);
        if (selectedGroupId) {
            await loadReceivers(selectedGroupId);
        }
    };

    const createGroup = async () => {
        const name = newGroupName.trim();
        if (!name) return;
        setCreatingGroup(true);
        try {
            const result : any = await postFetch("/dashboard/callcenter/groups/create", { name });
            if (result.message === "success") {
                toast.success("그룹을 만들었습니다", toastOptions);
                setAddGroupOpen(false);
                setNewGroupName("");
                await loadGroups(false);
                setSelectedGroupId(result.groupId);
            } else {
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다" : "그룹 생성 실패", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("그룹 생성 실패", toastOptions);
        }
        setCreatingGroup(false);
    };

    const queueNameMap : { [queue : string] : string } = {};
    queues.forEach((q : any) => { queueNameMap[q.queue] = q.name; });

    const hqReceivers = sortReceivers(receivers.filter((r) => r.receiverType === "hq"));
    const branchReceivers = sortReceivers(receivers.filter((r) => r.receiverType === "branch"));

    //큐 추가 후보: 교환기에 있고 이 그룹에 없는 큐 (다른 그룹 소속은 비활성 표시)
    const queueCandidates = pbxQueues.filter((q : any) => q.groupId !== selectedGroupId);
    const warningEntries = queues.filter((q : any) => queueWarnings[q.queue]);
    const missingPairQueues = queues.filter((q : any) => q.pairQueueExists === false);
    const queueMap : { [queue : string] : any } = {};
    queues.forEach((q : any) => { queueMap[q.queue] = q; });
    const modeAnchorQueue = modeAnchor ? queueMap[modeAnchor.queue] : null;

    const renderRows = (list : any[]) => list.map((r : any) => (
        <ReceiverRow
            key={r.id}
            receiver={r}
            now={now}
            pbxConnected={pbxConnected}
            queueName={r.branchQueue ? queueNameMap[r.branchQueue] : ""}
            busy={!!pendingIds[r.id]}
            managerMode={r.branchQueue && queueMap[r.branchQueue] ? queueMap[r.branchQueue].managerMode : "hq"}
            pairQueueExists={r.branchQueue && queueMap[r.branchQueue] ? queueMap[r.branchQueue].pairQueueExists !== false : true}
            onToggle={(checked : boolean) => toggleEnabled(r, checked)}
            onManager={(on : boolean) => toggleManager(r, on)}
            onModeToggle={(nextMode : string) => requestModeToggle(r, nextMode)}
            onOpenDetail={() => setDetailReceiver(r)}
        />
    ));

    return (
        <div className={styles.container}>
            {
                !pbxConnected ?
                    <div className={styles.banner}>
                        교환기(PBX)와 연결되어 있지 않습니다. 실시간 상태를 표시할 수 없고, 지금 바꾸는 설정은 교환기에 반영되지 않을 수 있습니다.
                    </div>
                    :
                    ""
            }

            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.pageTitle}>전화 수신 설정</span>
                    <span className={`${styles.pbxBadge} ${pbxConnected ? "" : styles.pbxBadgeOff}`}>
                        <span className={styles.dot} />{pbxConnected ? "교환기 연결됨" : "교환기 연결 끊김"}
                    </span>
                </div>
                <Button size="sm" variant="solid" startDecorator={<AddIcon />} onClick={() => setAddOpen(true)} disabled={!selectedGroupId}>
                    수신자 추가
                </Button>
            </div>

            <div className={styles.tabsRow}>
                <Tabs
                    value={selectedGroupId === null ? undefined : selectedGroupId}
                    onChange={(e : any, v : any) => { if (v !== null && v !== undefined) setSelectedGroupId(+v); }}
                >
                    <TabList>
                        {
                            groups.map((g : any) => (
                                <Tab key={g.id} value={g.id}>{g.name}</Tab>
                            ))
                        }
                    </TabList>
                </Tabs>
                {
                    canEditQueues ?
                        <Button size="sm" variant="outlined" color="neutral" onClick={() => setAddGroupOpen(true)}>+ 그룹</Button>
                        :
                        ""
                }
            </div>

            {
                loading ?
                    <div className={styles.loadingBox}><CircularProgress /></div>
                    :
                    groups.length === 0 ?
                        <div className={styles.empty}>등록된 큐 그룹이 없습니다.</div>
                        :
                        <>
                            <div className={styles.queueBar}>
                                <div className={styles.queueBarHead} onClick={() => setQueueBarOpen((o) => !o)}>
                                    <div className={styles.queueBarTitle}>
                                        {queueBarOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        <span>이 그룹이 받는 큐 · {queues.length}개</span>
                                    </div>
                                    <div className={styles.queueBarRule}>담당자 15초 → 전체 45초 → 안내 후 종료</div>
                                </div>
                                {
                                    queueBarOpen ?
                                        <div className={styles.queueBarBody}>
                                            {
                                                queues.map((q : any) => {
                                                    const missing = q.pairQueueExists === false;
                                                    const cls = [
                                                        styles.queueChip,
                                                        missing ? styles.queueChipMissing : q.managerMode === "branch" ? styles.queueChipBranch : "",
                                                        modeAnchor && modeAnchor.queue === q.queue ? styles.queueChipActive : ""
                                                    ].join(" ");
                                                    return (
                                                        <button
                                                            key={q.queue}
                                                            type="button"
                                                            className={cls}
                                                            onClick={(e) => setModeAnchor({ el: e.currentTarget, queue: q.queue })}
                                                            title={missing ? "담당자 큐가 교환기에 없습니다" : q.managerMode === "branch" ? "지점 직접 수신" : "본사 담당 모드"}
                                                        >
                                                            {missing ? "⚠ " : q.managerMode === "branch" ? "★ " : ""}{q.name}{q.name !== q.queue ? ` ${q.queue}` : ""}{queueWarnings[q.queue] ? " !" : ""} ▾
                                                        </button>
                                                    );
                                                })
                                            }
                                            {
                                                canEditQueues ?
                                                    (
                                                        addQueueOpen ?
                                                            <Select
                                                                size="sm"
                                                                placeholder="추가할 큐 선택"
                                                                listboxOpen
                                                                onClose={() => setAddQueueOpen(false)}
                                                                onChange={(e : any, v : any) => { if (v !== null) addQueue(v); }}
                                                            >
                                                                {
                                                                    queueCandidates.length === 0 ?
                                                                        <Option value="" disabled>추가할 수 있는 큐가 없습니다</Option>
                                                                        :
                                                                        queueCandidates.map((q : any) => (
                                                                            <Option key={q.queue} value={q.queue} disabled={!!q.groupId}>
                                                                                {q.name}{q.name !== q.queue ? ` (${q.queue})` : ""}{q.groupId ? ` — 다른 그룹 소속 (${q.groupName})` : ""}
                                                                            </Option>
                                                                        ))
                                                                }
                                                            </Select>
                                                            :
                                                            <button className={styles.addQueueChip} onClick={() => setAddQueueOpen(true)} disabled={!pbxConnected}>+ 큐 추가</button>
                                                    )
                                                    :
                                                    <div className={styles.lockNote}>
                                                        <LockOutlinedIcon style={{ fontSize: "13px", verticalAlign: "-2px", marginRight: "4px" }} />
                                                        큐 추가·제거는 권한이 있는 계정만 가능합니다
                                                    </div>
                                            }
                                            {
                                                missingPairQueues.map((q : any) => (
                                                    <div key={`missing-${q.queue}`} className={styles.warnLineDanger}>
                                                        ⚠ {q.name}: 담당자 큐({q.pairQueue} {q.pairQueueName})가 교환기에 없습니다 — 담당자 지정 불가
                                                    </div>
                                                ))
                                            }
                                            {
                                                groupWarnings.filter((w) => w.indexOf("담당자 큐(") === -1).map((w, i) => (
                                                    <div key={`gw-${i}`} className={styles.warnLine}>{w}</div>
                                                ))
                                            }
                                            {
                                                warningEntries.map((q : any) => (
                                                    <div key={q.queue} className={styles.warnLine}>
                                                        {q.name} 큐: {queueWarnings[q.queue].join(" / ")}
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        :
                                        ""
                                }
                            </div>

                            <div className={styles.section}>
                                <div className={styles.sectionTitle}>
                                    본사팀 <span className={styles.sectionCount}>전 지점 수신 · {hqReceivers.length}명 · 담당자 {managerCount}명</span>
                                </div>
                                <div className={styles.list} ref={hqListRef}>
                                    {
                                        hqReceivers.length === 0 ?
                                            <div className={styles.empty}>등록된 본사팀 수신자가 없습니다.</div>
                                            :
                                            renderRows(hqReceivers)
                                    }
                                </div>
                            </div>

                            <div className={styles.section}>
                                <div className={styles.sectionTitle}>
                                    지점 폰 <span className={styles.sectionCount}>자기 지점 큐만 수신 · {branchReceivers.length}대</span>
                                </div>
                                <div className={styles.list} ref={branchListRef}>
                                    {
                                        branchReceivers.length === 0 ?
                                            <div className={styles.empty}>등록된 지점 폰이 없습니다.</div>
                                            :
                                            renderRows(branchReceivers)
                                    }
                                </div>
                            </div>
                        </>
            }

            <AddReceiverModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                groupId={selectedGroupId}
                queues={queues}
                onAdded={() => { if (selectedGroupId) loadReceivers(selectedGroupId); }}
            />

            <ReceiverDetailModal
                open={!!detailReceiver}
                onClose={() => setDetailReceiver(null)}
                receiver={detailReceiver ? (receivers.find((r) => r.id === detailReceiver.id) || detailReceiver) : null}
                queueNames={queueNameMap}
                managerMode={detailReceiver && detailReceiver.branchQueue && queueMap[detailReceiver.branchQueue] ? queueMap[detailReceiver.branchQueue].managerMode : "hq"}
                onDeleted={(id : number) => {
                    setReceivers((prev) => prev.filter((r) => r.id !== id));
                    if (selectedGroupId) loadReceivers(selectedGroupId);
                }}
            />

            <QueuePasswordModal
                open={!!passwordQueue}
                onClose={() => setPasswordQueue(null)}
                groupId={selectedGroupId}
                queue={passwordQueue}
                onRemoved={onQueueRemoved}
            />

            <ManagerModePopover
                anchorEl={modeAnchor ? modeAnchor.el : null}
                queue={modeAnchorQueue}
                managerCount={managerCount}
                branchPhones={modeAnchorQueue ? receivers.filter((r) => r.receiverType === "branch" && r.branchQueue === modeAnchorQueue.queue) : []}
                canEditQueues={canEditQueues}
                onSave={saveManagerMode}
                onRemoveQueue={(q : any) => setPasswordQueue(q)}
                onClose={() => setModeAnchor(null)}
            />

            <ManagerModeConfirmModal
                open={!!modeConfirm}
                target={modeConfirm}
                onConfirm={saveManagerMode}
                onClose={() => setModeConfirm(null)}
            />

            <Modal open={addGroupOpen} onClose={() => setAddGroupOpen(false)}>
                <ModalDialog>
                    <div style={{ width: "300px" }}>
                        <div className={styles.modalTitle}>그룹 추가</div>
                        <div className={styles.modalLabel}>그룹 이름</div>
                        <Input
                            value={newGroupName}
                            placeholder="예: 지점 문의"
                            onChange={(e) => setNewGroupName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") createGroup(); }}
                            slotProps={{ input: { maxLength: 50 } }}
                        />
                        <div className={styles.modalHint}>그룹 = 같은 수신자 규칙을 공유하는 큐 묶음. 만든 뒤 큐를 추가하세요.</div>
                        <div className={styles.modalButtons}>
                            <Button variant="plain" color="neutral" onClick={() => setAddGroupOpen(false)} disabled={creatingGroup}>취소</Button>
                            <Button variant="solid" onClick={createGroup} loading={creatingGroup} disabled={!newGroupName.trim()}>만들기</Button>
                        </div>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default PhoneInboundSetting;
